import {Component, EventEmitter, Input, Output} from '@angular/core';
import {FormControl, Validators} from '@angular/forms';
import {countReloadAttempt} from '../../privacy-policy-content/privacy-policy-reload-count';
import {PrivacyPolicyVersionMatch} from '../../privacy-policy-content/privacy-policy-state';


@Component({
  selector: 'app-eula',
  templateUrl: './eula.component.html',
  styleUrls: ['./eula.component.scss']
})
export class EulaComponent {

  /**
   * Das Häkchen als FormControl, weil sich die Checkbox der Bibliothek nur darüber ausgrauen lässt;
   * ein eigenes disabled-Input bietet sie nicht.
   *
   * An requiredTrue erkennt sie ein Pflichtfeld und setzt das Sternchen an die Beschriftung.
   * Ausgewertet wird hier weiterhin der Wert, nicht die Gültigkeit.
   */
  readonly acceptedControl = new FormControl(false, Validators.requiredTrue);

  /**
   * Erscheint in der Statuszeile, wenn jemand ohne Häkchen zustimmen will.
   *
   * Nicht im message-Input der Checkbox: Die Bibliothek rendert ihn als schlichtes div, ohne
   * Live-Bereich und ohne Verknüpfung mit dem Eingabefeld - angesagt würde er damit nicht, und
   * der Fokus steht nach dem Versuch auf dem Knopf, nicht an der Checkbox.
   */
  acceptHint = '';

  @Input()
  showApproveButton = false;

  /**
   * Ob der angezeigte Text die geltende Fassung wiedergibt. Kommt von außen herein, weil nur der
   * Aufrufer den geladenen Kontext und eine Serverablehnung kennt.
   */
  @Input()
  set versionMatch(value: PrivacyPolicyVersionMatch) {
    this.versionMatch_ = value;
    this.syncControl();
  }

  get versionMatch(): PrivacyPolicyVersionMatch {
    return this.versionMatch_;
  }

  @Input()
  set busy(value: boolean) {
    this.busy_ = value;
    this.syncControl();
  }

  get busy(): boolean {
    return this.busy_;
  }

  @Output()
  approved = new EventEmitter<boolean>();

  private versionMatch_: PrivacyPolicyVersionMatch = 'matches';

  private busy_ = false;

  get blocked(): boolean {
    return this.versionMatch_ === 'deviates';
  }

  /**
   * Ein ausgegrauter Knopf nimmt keinen Fokus an, eine Vorleseanwendung erreicht ihn also nicht.
   * Damit der Grund auffindbar bleibt, steht er als sichtbarer Text in der Statuszeile - bei
   * abweichender Fassung zusätzlich im Hinweis über der Erklärung.
   */
  get disabled(): boolean {
    return this.blocked || this.busy_;
  }

  /**
   * Der Knopf sagt im Ladezustand, was gerade passiert - ohne das bliebe ein Klick ohne Antwort,
   * der Knopf würde nur grau.
   */
  get approveText(): string {
    return this.busy_ ? 'Wird gesendet …' : 'Datenschutzerklärung akzeptieren';
  }

  /**
   * Was die Statuszeile zeigt.
   *
   * Die Reihenfolge ist die ihrer Dringlichkeit: Eine abweichende Fassung verhindert die
   * Zustimmung ganz, eine laufende Anfrage verschiebt sie, ein fehlendes Häkchen ist eine
   * Nachfrage. Es kann ohnehin nur eines zutreffen, weil die ersten beiden die Bedienelemente
   * ausgrauen.
   *
   * Trifft keines zu, erklärt die Zeile das Sternchen an der Beschriftung - WCAG-Technik H90
   * verlangt, dass die Bedeutung eines solchen Zeichens dasteht. Deshalb beginnt auch jede andere
   * Meldung mit dem Sternchen: Die Zeile ist die Fußnote zum markierten Feld und darf es in keinem
   * Zustand unerklärt lassen.
   */
  get statusMessage(): string {
    if (this.blocked) {
      return '* Bestätigen ist nicht möglich, solange die angezeigte Fassung nicht die geltende ist.';
    }
    if (this.busy_) {
      return '* Ihre Bestätigung wird gesendet.';
    }
    if (this.acceptHint) {
      return this.acceptHint;
    }
    return '* Pflichtfeld';
  }

  /**
   * Ob die Zeile als Warnung erscheint. Der erklärende Satz und die laufende Anfrage sind
   * Sachauskünfte; ohne eigene Farbe für sie wäre der Wechsel zu einer echten Warnung nicht zu
   * erkennen. Ein zurückgenommenes Häkchen zählt mit, weil die Bibliothek Feld und Beschriftung
   * dann von sich aus rot färbt - eine schwarze Zeile daneben widerspräche dem.
   */
  get statusIsWarning(): boolean {
    return this.blocked || !!this.acceptHint || this.acceptedWasWithdrawn;
  }

  /**
   * Spiegelt die Bedingung, unter der die Bibliothek ihre eigene Fehlfarbe zeigt - nachgebildet,
   * weil sie den Zustand nicht nach außen gibt.
   */
  private get acceptedWasWithdrawn(): boolean {
    return this.acceptedControl.touched
      && this.acceptedControl.enabled
      && !this.acceptedControl.value;
  }

  /**
   * Das Neuladen gehört zum Einbinder, nicht zum Hinweis - siehe VersionNoticeComponent.
   */
  onReloadRequested(): void {
    countReloadAttempt();
    this.reloadPage();
  }

  /**
   * Ausgelagert, damit der Aufruf im Test beobachtbar bleibt: window.location ist in Chrome nicht
   * als ersetzbar deklariert, ein Spion darauf scheitert.
   */
  protected reloadPage(): void {
    window.location.reload();
  }

  onAccepted(
    accepted: boolean,
  ): void {
    if (accepted) {
      this.acceptHint = '';
    }
  }

  approve(): void {
    if (this.disabled) {
      return;
    }
    if (this.acceptedControl.value) {
      this.approved.emit(true);
    } else {
      this.acceptHint = '* Bitte bestätigen Sie, dass Sie die Datenschutzerklärung gelesen haben.';
    }
  }

  private syncControl(): void {
    if (this.disabled) {
      // INFO: Die Nachfrage nach dem Häkchen verfällt, sobald die Zustimmung ohnehin nicht möglich
      // ist - sonst meldete die Zeile einen Grund, der nicht mehr gilt.
      this.acceptHint = '';
      this.acceptedControl.disable({emitEvent: false});
    } else {
      this.acceptedControl.enable({emitEvent: false});
    }
  }
}
